import { EnrollmentStatus } from '@zorro/clients';
import { $enum } from 'ts-enum-util';

import {
  ActiveCoverageIcon,
  CarrierApplicationSentIcon,
  CoverageEndedIcon,
  DeadlinePassedIcon,
  ElectionActiveHasNotStartedIcon,
  ElectionActiveStartedIcon,
  ElectionSubmittedIcon,
  EnrollmentConfirmedIcon,
  NoEnrollmentsIcon,
  PendingElectionWindowIcon,
  WaivedCoverageIcon,
  WaivedElectionIcon,
} from './StatusIcons';

export type EnrollmentStatusView = {
  label: string;
  tooltipLabel?: string;
  renderIcon: (
    size?: string | number,
    withBackground?: boolean
  ) => React.ReactElement;
};

export type EnrollmentStatusViewer = 'employee' | 'employerAdmin' | 'ops';

type ViewerTooltips = {
  employee?: string;
  employerAdmin?: string;
  ops?: string;
};

const getTooltipLabel = (
  viewer: EnrollmentStatusViewer,
  { employee, employerAdmin, ops = employerAdmin }: ViewerTooltips
) => {
  const tooltipByViewer: Record<EnrollmentStatusViewer, string | undefined> = {
    employee,
    employerAdmin,
    ops,
  };
  return tooltipByViewer[viewer];
};

export const getEnrollmentStatusView = (
  enrollmentStatus: EnrollmentStatus,
  viewer: EnrollmentStatusViewer = 'employerAdmin'
): EnrollmentStatusView =>
  $enum.mapValue(enrollmentStatus).with<EnrollmentStatusView>({
    [EnrollmentStatus.PENDING_ELECTION_WINDOW]: {
      label: 'Pending election window',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: "Your election window hasn't opened yet",
        employerAdmin: 'The election period has not started yet',
      }),
      renderIcon: (size, withBackground) => (
        <PendingElectionWindowIcon
          size={size}
          withBackground={withBackground}
        />
      ),
    },
    [EnrollmentStatus.ELECTION_ACTIVE]: {
      label: 'Election active',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your election window is open for you to select a plan',
      }),
      renderIcon: (size, withBackground) => (
        <ElectionActiveStartedIcon
          size={size}
          withBackground={withBackground}
        />
      ),
    },
    [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: {
      label: "Election active: Hasn't started",
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your election window is open for you to select a plan',
        employerAdmin:
          'The election period is active, but the employee has not logged in yet',
      }),
      renderIcon: (size, withBackground) => (
        <ElectionActiveHasNotStartedIcon
          size={size}
          withBackground={withBackground}
        />
      ),
    },
    [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: {
      label: 'Election active: Started',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your election window is open for you to select a plan',
        employerAdmin:
          'The election period is active, and the employee has logged in but has not made a selection',
      }),
      renderIcon: (size, withBackground) => (
        <ElectionActiveStartedIcon
          size={size}
          withBackground={withBackground}
        />
      ),
    },
    [EnrollmentStatus.ELECTION_SUBMITTED]: {
      label: 'Election submitted',
      tooltipLabel: getTooltipLabel(viewer, {
        employee:
          "You've submitted your plan. Enrolling with your insurance carrier is the next step",
        employerAdmin:
          'The employee submitted their plan selection. Carrier enrollment is the next step',
        ops: "Check the plan status for whether it's Ready to apply or Awaiting employee",
      }),
      renderIcon: (size, withBackground) => (
        <ElectionSubmittedIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.WAIVED_ELECTION]: {
      label: 'Waived election',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'You chose to waive coverage during enrollment',
        employerAdmin: 'The employee has chosen to waive coverage',
      }),
      renderIcon: (size, withBackground) => (
        <WaivedElectionIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.DEADLINE_PASSED]: {
      label: 'Deadline passed',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your election window closed without a selection',
        employerAdmin:
          'The election period ended without a plan selection from the employee',
      }),
      renderIcon: (size, withBackground) => (
        <DeadlinePassedIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.ENROLLMENT_CONFIRMED]: {
      label: 'Enrollment confirmed',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your enrollment has been confirmed by the carrier',
        employerAdmin:
          "The employee's enrollment has been finalized and confirmed by the carrier",
      }),
      renderIcon: (size, withBackground) => (
        <EnrollmentConfirmedIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: {
      label: 'Enrollment in process',
      tooltipLabel: getTooltipLabel(viewer, {
        employee:
          "Your enrollment is being finalized with your insurance carrier. We'll let you know once it's confirmed",
        employerAdmin:
          "The employee's enrollment is being finalized with the carrier. No action needed from you",
        ops: "Check the plan status for whether it's Carrier application sent or Awaiting payment",
      }),
      renderIcon: (size, withBackground) => (
        <CarrierApplicationSentIcon
          size={size}
          withBackground={withBackground}
        />
      ),
    },
    [EnrollmentStatus.COVERAGE_ENDED]: {
      label: 'Coverage ended',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'The coverage for the defined period has ended',
        employerAdmin:
          "The employee's coverage under the selected plan has ended",
      }),
      renderIcon: (size, withBackground) => (
        <CoverageEndedIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.ACTIVE_COVERAGE]: {
      label: 'Active coverage',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'Your coverage under the selected plan is currently active',
        employerAdmin:
          "The employee's coverage under the selected plan is currently active",
      }),
      renderIcon: (size, withBackground) => (
        <ActiveCoverageIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.WAIVED_COVERAGE]: {
      label: 'Waived coverage',
      tooltipLabel: getTooltipLabel(viewer, {
        employee: 'You chose to waive coverage for this period',
        employerAdmin: 'The employee has chosen to waive coverage',
      }),
      renderIcon: (size, withBackground) => (
        <WaivedCoverageIcon size={size} withBackground={withBackground} />
      ),
    },
    [EnrollmentStatus.NO_ENROLLMENTS]: {
      label: 'No enrollments',
      renderIcon: (size, withBackground) => (
        <NoEnrollmentsIcon size={size} withBackground={withBackground} />
      ),
    },
  });
